// UI state for the catalog (jotai). Server state lives in TanStack Query (see queries.ts).

import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { initialLocale, LOCALE_STORAGE_KEY, type CatalogLocale } from './locale';

import type { SortState } from './engine';
import type { ActiveFilters } from './types';

// The chosen GUI language, persisted in localStorage: a user who switched to English should still
// get English on the next visit (see chat/screenshot: "Sprache, Company speichern"; the brand is
// remembered the same way, see branding.ts). Until they pick one, the browser's own preference -
// or, for a logged-in user, the language from the token - decides (see UserLocaleSync).
//
// `getOnInit: true` because the stored value must be there for the FIRST render: without it jotai
// starts from the default and hydrates on mount, which would flash German at an English user.
export const catalogLocaleAtom = atomWithStorage<CatalogLocale>(LOCALE_STORAGE_KEY, initialLocale(), undefined, {
    getOnInit: true
});
export const searchAtom = atom<string>('');
// Toggle next to the search box (Toolbar): search products (tree/facets/grid) or search
// documents catalog-wide (DocumentSearchResults) - reuses searchAtom/pageAtom/perPageAtom for both.
export const searchModeAtom = atom<'products' | 'documents'>('products');
export const selectedNodeIdAtom = atom<string | undefined>(undefined);
// Document-mode tree selection - kept separate from selectedNodeIdAtom (product mode) so
// switching searchMode doesn't clobber either view's own category selection.
export const selectedDocumentNodeIdAtom = atom<string | undefined>(undefined);
// More than one Gruppe/Familie/System picked on the landing page (SearchMask's multi-selects): a
// SCOPE, not a selection - CatalogPage cuts index and documents down to the union of these
// subtrees before anything else runs (see engine.inAnyCategory), shows the chosen nodes above the
// breadcrumb and offers one "aufheben" to widen again. Exactly one picked node goes the normal
// selectedNodeIdAtom route instead and leaves this empty. Only the landing page ever sets it
// (customer decision: no permanent "System" facet in the drawer).
export const scopeNodeIdsAtom = atom<string[]>([]);
// Document-mode-only facet filters (Sprache/Dokumentenart) - global atoms so SearchMask's own
// "Mehr Filteroptionen" (landing page) and CatalogPage's DocumentFacetsPanel (results page)
// share the same selection instead of the landing page's choice getting silently
// dropped on navigate, same reasoning as selectedDocumentNodeIdAtom above.
export const documentLanguageAtom = atom<string | null>(null);
export const documentDocTypesAtom = atom<string[]>([]);
// Edition dates ("MM/YYYY", see MediaItem.editionDate) - third document facet next to
// Sprache/Dokumenttyp. Only the results page's DocumentFacetsPanel offers UI for it; SearchMask
// resets it on submit so the landing page's Treffer promise never silently carries a hidden
// edition restriction.
export const documentEditionsAtom = atom<string[]>([]);
export const filtersAtom = atom<ActiveFilters>({});
// Facet groups the user folded away in the filter drawer (keys: the product facet key, or the
// fixed `doc:*` ids of DocumentFacetsPanel). Global rather than component state because the drawer
// unmounts its content on close - see components/FacetGroup.tsx.
export const collapsedFacetGroupsAtom = atom<string[]>([]);
// Branches open in the category tree (NavigationTree): opened and closed by the user, and never
// closed by a click elsewhere (reported: "Ein Klick auf einen Menüpunkt schließt im Moment die
// anderen Knoten"). Global so the mobile tree drawer reopens the way it was left.
export const expandedTreeNodesAtom = atom<string[]>([]);
export const sortAtom = atom<SortState>({ key: 'name', dir: 'asc' });
export const viewModeAtom = atom<'grid' | 'list'>('list');
export const pageAtom = atom<number>(1);
// Starts at 100 per Fachbereich feedback ("Wichtig ist, dass wir bei mindestens 100 Ergebnissen
// pro Seite starten") - see the perPage Select's options in CatalogPage.
// 50, not 100: a page renders one row (and one thumbnail request, see ProductImage) per item, and
// nobody scans a hundred rows before paging. Larger sizes stay available in the picker.
export const perPageAtom = atom<number>(50);

// List view: extra attribute columns, per-column free-text filters, and filter-row visibility.
export const listColumnsAtom = atom<string[]>([]);
export const columnFiltersAtom = atom<Record<string, string>>({});
